/** Keep the current complete painting visible until all replacement poses load. */
import { useEffect, useMemo, useState } from 'react';
import type { AvatarSpec } from './avatar';
import { preload, render, renderHead, type PaintedOptions } from './painted';

export function usePaintedSvg(spec: Partial<AvatarSpec> | null | undefined, options: PaintedOptions = {}): string {
  const request = JSON.stringify({ spec, options });
  const [svg, setSvg] = useState('');
  const input = useMemo(() => JSON.parse(request) as { spec: typeof spec; options: PaintedOptions }, [request]);
  useEffect(() => {
    let current = true;
    preload(input.spec, input.options).then(() => {
      if (!current) return;
      const next = input.options.headOnly ? renderHead(input.spec, input.options) : render(input.spec, input.options);
      setSvg(next.replace('<svg ', '<svg data-art-ready="true" '));
    }).catch(() => {
      // Retain the last complete image if loading fails. But with nothing
      // drawn yet, one missing file must not leave a child with no character
      // at all (audit, 2026-10-12): draw what there is.
      if (current) setSvg((shown) => {
        if (shown) return shown;
        const next = input.options.headOnly ? renderHead(input.spec, input.options) : render(input.spec, input.options);
        return next;
      });
    });
    return () => { current = false; };
  }, [input]);
  return svg;
}
